/**
 * In-memory cache for local-library getAlbum IPC responses.
 * Avoids redundant ~5s SQLite-contended IPC on re-open, recents validation, and cover hydration.
 */

import { normalizeLocalAlbumId } from './localLibraryMetadata';

export type CachedLocalAlbumDetail = {
  success: boolean;
  album?: Record<string, unknown>;
  tracks?: Record<string, unknown>[];
  version?: number;
};

const cache = new Map<string, CachedLocalAlbumDetail>();
let invalidationInitialized = false;

function normalizeKey(albumId: string): string {
  return normalizeLocalAlbumId(String(albumId || '').trim());
}

export function getCachedLocalAlbumDetail(albumId: string): CachedLocalAlbumDetail | null {
  const key = normalizeKey(albumId);
  if (!key) return null;
  return cache.get(key) ?? null;
}

export function setCachedLocalAlbumDetail(albumId: string, detail: CachedLocalAlbumDetail): void {
  const key = normalizeKey(albumId);
  if (!key || !detail?.success) return;
  cache.set(key, detail);
}

export function invalidateCachedLocalAlbumDetail(albumId: string): void {
  const key = normalizeKey(albumId);
  if (key) cache.delete(key);
}

export function clearCachedLocalAlbumDetails(): void {
  cache.clear();
}

export function getCachedLocalAlbumCoverMeta(albumId: string): {
  embedded_cover_hash: string | null;
  folder_cover_hash: string | null;
  library_folder_path: string | null;
} | null {
  const detail = getCachedLocalAlbumDetail(albumId);
  const row = detail?.album;
  if (!row) return null;
  const emb = (row.embedded_cover_hash ?? row.embeddedCoverHash ?? null) as string | null;
  const fol = (row.folder_cover_hash ?? row.folderCoverHash ?? null) as string | null;
  const folderPath = (row.library_folder_path ?? row.libraryFolderPath ?? null) as string | null;
  if (!emb && !fol && !folderPath) return null;
  return {
    embedded_cover_hash: emb,
    folder_cover_hash: fol,
    library_folder_path: folderPath,
  };
}

/** Wire cache invalidation to the existing library change / rescan signal. */
export function ensureAlbumDetailCacheInvalidation(): void {
  if (invalidationInitialized || typeof window === 'undefined') return;
  invalidationInitialized = true;

  window.addEventListener('maestro:library:changed', (event) => {
    const detail = (event as CustomEvent)?.detail as {
      kind?: string;
      id?: string;
      reason?: string;
      catalogReason?: string;
    } | undefined;

    if (detail?.kind === 'album' && detail?.id) {
      invalidateCachedLocalAlbumDetail(detail.id);
      return;
    }

    if (detail?.reason === 'connector_catalog_changed' && detail?.catalogReason === 'cover_maintain') {
      clearCachedLocalAlbumDetails();
      return;
    }

    clearCachedLocalAlbumDetails();
  });
}
